import { TabGroup } from "@headlessui/react";
import { type ReactNode, useState } from "react";
import { NavigationTabContext } from "./NavigationTabContext";

export type NavigationTabGroupProps<Tabs extends readonly string[]> = {
  children: ReactNode;
  tabs: Tabs;
  panelFactory: (tab: Tabs[number]) => ReactNode;
  value?: Tabs[number];
  defaultValue?: Tabs[number];
  onValueChange?: (tab: Tabs[number]) => void;
};

export const NavigationTabGroup = <Tabs extends readonly string[]>({
  children,
  tabs,
  panelFactory,
  value,
  defaultValue,
  onValueChange,
}: NavigationTabGroupProps<Tabs>) => {
  const firstTab = tabs[0];
  if (!firstTab) throw new Error("Cannot find first tab");

  const [uncontrolledTabName, setUncontrolledTabName] = useState<Tabs[number]>(
    defaultValue ?? firstTab,
  );

  const selectedTabName = tabs.includes(value ?? uncontrolledTabName)
    ? (value ?? uncontrolledTabName)
    : firstTab;
  const tabIndex = tabs.indexOf(selectedTabName);

  // Keep uncontrolled state valid when the tab list changes (adjust during render).
  if (value === undefined && uncontrolledTabName !== selectedTabName) {
    setUncontrolledTabName(selectedTabName);
  }

  return (
    <NavigationTabContext.Provider
      value={{ tabs, factory: panelFactory, tabName: selectedTabName }}
    >
      <TabGroup
        selectedIndex={tabIndex}
        onChange={(index) => {
          const nextTabName = tabs[index] ?? firstTab;
          if (value === undefined) {
            setUncontrolledTabName(nextTabName);
          }
          onValueChange?.(nextTabName);
        }}
      >
        {children}
      </TabGroup>
    </NavigationTabContext.Provider>
  );
};
